import { Form, FormInstance, Select } from 'antd';
import { Store } from 'antd/lib/form/interface';
import React from 'react';

import { useMutation } from '@common/hooks';
import { ComplianceItemUploadInput } from '@onboarding/components/compliance-item-files-list';
import { ComplianceItemInputExpirationDate } from '@onboarding/components/compliance-item-input-expiration-date';
import { ComplianceItemType } from '@onboarding/graphql';
import {
  CreateNurseAssessmentMutation,
  UpdateNurseAssessmentMutation,
} from 'works/graphql/nurse-assessment';
import { formatToYearMonthDay } from 'works/legacy/lib/date-helper';
import { addErrorsToForm } from 'works/legacy/lib/form-helpers';
import { createMessage } from 'works/legacy/lib/message-helper';

type Props = {
  form: FormInstance;
  complianceItem: ComplianceItemType;
  refetchQueries: string[];
  onSubmit?: () => void;
  onSuccess?: () => void;
  onError?: () => void;
};

export function ComplianceItemAddAttachmentFormAssessment({
  form,
  complianceItem,
  refetchQueries,
  onSubmit,
  onSuccess,
  onError,
}: Props) {
  const { detailItem } = complianceItem;

  const errorMessage = () =>
    createMessage('error', 'Failed to update assessment. Please try again.');

  const [updateNurseAssessment, { loading: formSubmitting }] = useMutation(
    UpdateNurseAssessmentMutation,
    {
      refetchQueries,
      onCompleted: (response) => {
        if (response?.updateNurseAssessment.errors) {
          addErrorsToForm(form, response.updateNurseAssessment.errors);
          errorMessage();
          onError?.();
        } else {
          onSuccess?.();
          createMessage('success', 'Assessment has been successfully updated.');
        }
      },
      onError: () => {
        errorMessage();
        onError?.();
      },
    }
  );

  const [createNurseAssessment] = useMutation(CreateNurseAssessmentMutation, {
    refetchQueries,
    onCompleted: (response) => {
      if (response?.createNurseAssessment.errors) {
        addErrorsToForm(form, response.updateNurseAssessment.errors);
        errorMessage();
        onError?.();
      } else {
        onSuccess?.();
        createMessage('success', 'Assessment has been successfully created.');
      }
    },
    onError: () => {
      errorMessage();
      onError?.();
    },
  });

  const onFinish = (values: Store) => {
    if (formSubmitting) return;

    onSubmit?.();

    if (!detailItem) {
      createNurseAssessment({
        variables: {
          worksListItemId: complianceItem.id,
          worksProfileId:
            complianceItem.worksList.staffingRequestAssignment.worksProfile.id,
          assessmentId: complianceItem.assessment?.id,
          input: {
            expiresOn: formatToYearMonthDay(values.expiresOn),
            completedOn: formatToYearMonthDay(values.completedOn),
          },
          issuerId: values.issuerId,
          files: values.files ? values.files.fileList : null,
        },
      });

      return;
    }

    updateNurseAssessment({
      variables: {
        id: complianceItem.id,
        input: {
          completedOn: formatToYearMonthDay(values.completedOn),
          expiresOn: formatToYearMonthDay(values.expiresOn),
        },
        issuerId: values.issuerId,
        files: values.files?.fileList ?? [],
      },
    });
  };

  return (
    <Form onFinish={onFinish} layout="vertical" form={form}>
      {detailItem?.name && (
        <Form.Item label="AssessmentName">{detailItem.name}</Form.Item>
      )}

      <ComplianceItemInputExpirationDate
        expirationType={complianceItem.expirationType}
      />

      <Form.Item label="Accepted Issuer(s)">
        {complianceItem.assessmentIssuers
          ?.map((issuer) => issuer.name)
          .join(', ')}
      </Form.Item>

      <Form.Item
        label="Issuer Selected"
        name="issuerId"
        rules={[{ required: true, message: 'Please select an issuer' }]}
      >
        <Select size="large">
          {complianceItem.assessmentIssuersToBeSelected?.map((issuer) => {
            return (
              <Select.Option key={issuer.id} value={issuer.id}>
                {issuer.name}
              </Select.Option>
            );
          })}
        </Select>
      </Form.Item>

      <ComplianceItemUploadInput multiple required />
    </Form>
  );
}
